import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useRecoilValue } from 'recoil';

import useGetContractDetails from '@/hooks/contract/useGetContractDetails.hook';
import Headers from './components/ContractHeaders/Headers';
import useUpdateContract from './hooks/useUpdateContract';
import { useGetUserRole } from '@/hooks/identity/useGetUserRole';
import { UserRole } from '@/config/roles.config';
import { isContractOneEvoMigrated } from '@/utils/oneEvoMigration.utils';
import { useGetLatestFieldVerifications } from './hooks/useGetLatestFieldVerifications';
import { useSearchUsers } from '@/hooks/identity/useSearchUsers';
import { User } from '@/hooks/identity/responseModels/user';
import { fieldVerificationRefreshTrigger } from '../ContractIq/ContractIq.state';

export default function ContractHeadersContainer() {
  const { contractId }: { contractId: UUID } = useParams();
  const { contract, isContractLoading } = useGetContractDetails(contractId);
  const { updateContract, isLoadingUpdateContract } = useUpdateContract(contractId);
  const { getUserRole } = useGetUserRole();
  const [userRole, setUserRole] = useState<UserRole>();
  const refreshTrigger = useRecoilValue(fieldVerificationRefreshTrigger);
  const { getLatestFieldVerifications, fieldVerificationMap } = useGetLatestFieldVerifications();
  const { searchUsers } = useSearchUsers();
  const [verificationUsers, setVerificationUsers] = useState<User[]>([]);
  const fetchedUserIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const getRole = async () => {
      const roleName = await getUserRole();
      setUserRole(roleName);
    };
    getRole();
  }, [getUserRole]);

  useEffect(() => {
    if (contractId) {
      getLatestFieldVerifications(contractId);
    }
  }, [contractId, getLatestFieldVerifications, refreshTrigger]);

  useEffect(() => {
    const userIds = Object.values(fieldVerificationMap).map((v) => v.userID);
    const uniqueNewUserIds = [...new Set(userIds)].filter(
      (id) => !fetchedUserIdsRef.current.has(id)
    );

    if (uniqueNewUserIds.length > 0) {
      uniqueNewUserIds.forEach((id) => fetchedUserIdsRef.current.add(id));
      searchUsers({ ids: uniqueNewUserIds }).then((searchResult) => {
        setVerificationUsers((prev) => {
          const existingUserIds = new Set(prev.map((u) => u.Id));
          const newUsers = searchResult.filter((u) => !existingUserIds.has(u.Id));
          return [...prev, ...newUsers];
        });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldVerificationMap]);

  const contractStatus = contract?.status?.status;
  const isReaderRole = userRole === UserRole.READER;
  const isUserRole = userRole === UserRole.USER;
  const userAllowedStatuses = ['Active', 'ActiveDNR', 'Terminating'];
  const isUserStatusAllowed = isUserRole && contractStatus !== undefined &&
    userAllowedStatuses.includes(contractStatus);

  const isStatusBasedDisabled = isContractLoading ||
    contractStatus === 'ActiveDNR' ||
    contractStatus === 'Cancelled' ||
    contractStatus === 'Expired' ||
    contractStatus === 'Inactive' ||
    contractStatus === 'Terminated';

  const isDisabledEditField = isReaderRole ||
    isContractOneEvoMigrated(contract) ||
    (isUserRole ? !isUserStatusAllowed : isStatusBasedDisabled);

  return (
    <div className="mb-20">
      <Headers
        isLoading={isContractLoading || isLoadingUpdateContract}
        contract={contract}
        onSubmit={updateContract}
        isDisabledEditField={isDisabledEditField}
        fieldVerificationMap={fieldVerificationMap}
        verificationUsers={verificationUsers}
      />
    </div>
  );
}
